(() => {
  const widgets = Array.from(document.querySelectorAll('[data-yotpo-api-widget]'));

  if (!widgets.length) return;

  const bottomlineCache = new Map();
  const reviewsCache = new Map();
  const productFiltersCache = new Map();
  const reviewModalCloseTimers = new WeakMap();
  const locale = document.documentElement.lang || 'en-US';
  const reviewModalOpenClass = 'yotpo-review-modal-is-open';

  const getNumber = (value, fallback = 0) => {
    const number = Number(value);

    return Number.isFinite(number) ? number : fallback;
  };

  const escapeHtml = (value) => {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  const escapeAttribute = (value) => {
    return escapeHtml(value).replace(/`/g, '&#96;');
  };

  const formatCount = (value) => {
    try {
      return new Intl.NumberFormat(locale).format(getNumber(value));
    } catch (error) {
      return String(getNumber(value));
    }
  };

  const pluralize = (count, singular, plural = `${singular}s`) => {
    return getNumber(count) === 1 ? singular : plural;
  };

  const formatDate = (value) => {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return '';

    try {
      return new Intl.DateTimeFormat(locale, {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }).format(date);
    } catch (error) {
      return date.toLocaleDateString();
    }
  };

  const getAppKey = (widget) => widget.dataset.yotpoAppKey || '';
  const getProductId = (widget) => widget.dataset.yotpoProductId || '';

  const getReviewsUrl = ({ appKey, productId, page = 1, perPage = 5 }) => {
    const url = new URL(`https://api-cdn.yotpo.com/v3/storefront/store/${appKey}/product/${productId}/reviews`);

    url.searchParams.set('page', String(page));
    url.searchParams.set('perPage', String(perPage));

    return url.toString();
  };

  const getProductFiltersUrl = ({ appKey, productId }) => {
    return `https://api-cdn.yotpo.com/v3/storefront/store/${encodeURIComponent(appKey)}/product/${encodeURIComponent(productId)}/productFilters`;
  };

  const fetchYotpoJson = async (url, options = {}) => {
    const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
    const isUrlEncoded = typeof URLSearchParams !== 'undefined' && options.body instanceof URLSearchParams;
    const response = await fetch(url, {
      ...options,
      headers: {
        Accept: 'application/json',
        ...(options.body && !isFormData
          ? { 'Content-Type': isUrlEncoded ? 'application/x-www-form-urlencoded;charset=UTF-8' : 'application/json' }
          : {}),
        ...(options.headers || {})
      }
    });

    if (!response.ok) {
      throw new Error(`Yotpo request failed with ${response.status}`);
    }

    const payload = await response.json();

    if (payload?.status?.code && payload.status.code >= 400) {
      throw new Error(payload.status.message || 'Yotpo request failed');
    }

    return payload.response || payload || {};
  };

  const getProductFilters = (appKey, productId) => {
    const cacheKey = `${appKey}:${productId}`;

    if (!productFiltersCache.has(cacheKey)) {
      const request = fetchYotpoJson(getProductFiltersUrl({ appKey, productId })).catch((error) => {
        productFiltersCache.delete(cacheKey);
        throw error;
      });

      productFiltersCache.set(cacheKey, request);
    }

    return productFiltersCache.get(cacheKey);
  };

  const getBottomline = (appKey, productId) => {
    const cacheKey = `${appKey}:${productId}`;

    if (!bottomlineCache.has(cacheKey)) {
      bottomlineCache.set(
        cacheKey,
        fetchYotpoJson(getReviewsUrl({ appKey, productId, page: 1, perPage: 1 }))
      );
    }

    return bottomlineCache.get(cacheKey);
  };

  const getReviewsPage = ({ appKey, productId, page, perPage, star }) => {
    const cacheKey = `${appKey}:${productId}:${page}:${perPage}:${star || 'all'}`;

    if (!reviewsCache.has(cacheKey)) {
      const requestOptions = star
        ? {
          method: 'POST',
          body: JSON.stringify({ scores: [getNumber(star)] })
        }
        : {};

      reviewsCache.set(cacheKey, fetchYotpoJson(getReviewsUrl({ appKey, productId, page, perPage }), requestOptions));
    }

    return reviewsCache.get(cacheKey);
  };

  const normalizeBottomline = (bottomline = {}) => {
    return {
      averageScore: getNumber(bottomline.averageScore ?? bottomline.average_score),
      totalReviews: getNumber(
        bottomline.totalReviews ?? bottomline.total_reviews ?? bottomline.totalReview ?? bottomline.total_review
      ),
      starDistribution: bottomline.starDistribution || bottomline.star_distribution || {}
    };
  };

  const setStars = (element, score) => {
    if (!element) return;

    const averageScore = Math.min(Math.max(getNumber(score), 0), 5);
    const percent = (averageScore / 5) * 100;

    element.style.setProperty('--rating-percent', `${percent}%`);
    element.setAttribute('aria-label', `${averageScore.toFixed(1)} out of 5 stars`);
  };

  const updateProductReviewBadges = (productId, bottomline) => {
    if (!productId) return;

    document.querySelectorAll('[data-product-review-badge]').forEach((badge) => {
      if (badge.dataset.productId !== String(productId)) return;

      const rating = badge.querySelector('[data-product-review-badge-rating]');
      const count = badge.querySelector('[data-product-review-badge-count]');

      if (!bottomline) {
        badge.hidden = true;
        return;
      }

      const totalReviews = getNumber(bottomline.totalReviews);
      const averageScore = Math.min(Math.max(getNumber(bottomline.averageScore), 0), 5);

      if (!totalReviews) {
        if (rating) rating.textContent = '';
        if (count) count.textContent = '';
        badge.removeAttribute('aria-label');
        badge.hidden = true;
        return;
      } else {
        if (rating) rating.textContent = averageScore ? averageScore.toFixed(1) : '';
        if (count) count.textContent = `(${formatCount(totalReviews)})`;
        badge.setAttribute(
          'aria-label',
          `${averageScore ? `${averageScore.toFixed(1)} out of 5 stars, ` : ''}${formatCount(totalReviews)} ${pluralize(totalReviews, 'review')}. Jump to customer reviews.`
        );
      }

      badge.hidden = false;
    });
  };

  const setStatus = (widget, message = '') => {
    const status = widget.querySelector('[data-yotpo-status]');

    if (!status) return;

    status.textContent = message;
    status.hidden = !message;
  };

  const setReviewFeedback = (form, message = '') => {
    const feedback = form?.querySelector('[data-yotpo-review-feedback]');

    if (!feedback) return;

    feedback.textContent = message;
    feedback.hidden = !message;
  };

  const setReviewFormLoading = (form, isLoading) => {
    const submitButton = form?.querySelector('[data-yotpo-review-submit]');

    if (!submitButton) return;

    submitButton.disabled = isLoading;
    submitButton.classList.toggle('is-loading', isLoading);
    submitButton.setAttribute('aria-busy', String(isLoading));
  };

  const resetReviewForm = (widget) => {
    const form = widget.querySelector('[data-yotpo-review-form]');
    const success = widget.querySelector('[data-yotpo-review-success]');

    if (!form) return;

    form.hidden = false;
    success.hidden = true;
    form.reset();
    form.querySelectorAll('[data-yotpo-review-custom-select]').forEach((customSelect) => {
      const valueLabel = customSelect.querySelector('[data-yotpo-review-custom-select-value]');
      const input = customSelect.closest('[data-yotpo-review-question]')
        ?.querySelector('[data-yotpo-review-custom-select-input]');

      if (input instanceof HTMLInputElement) input.value = '';
      if (valueLabel) valueLabel.textContent = 'Select an option';

      customSelect.querySelectorAll('[data-yotpo-review-custom-option]').forEach((option) => {
        option.setAttribute('aria-selected', 'false');
      });

      if (customSelect instanceof HTMLDetailsElement) customSelect.open = false;
    });
    setReviewFeedback(form, '');
    setReviewFormLoading(form, false);
    updateReviewRatingState(form);
  };

  const updateReviewRatingState = (form) => {
    const selected = getNumber(form?.querySelector('input[name="review_score"]:checked')?.value);

    form?.querySelectorAll('[data-yotpo-rating-option]').forEach((option) => {
      const input = option.querySelector('input');
      const value = getNumber(input?.value);

      option.classList.toggle('is-selected', Boolean(selected && value <= selected));
    });
  };

  const getReviewQuestionId = (question = {}) => {
    return String(question.questionId ?? question.question_id ?? '').replace(/[^a-zA-Z0-9_-]/g, '');
  };

  const getProductSizeOptions = (widget) => {
    const data = widget.querySelector('[data-yotpo-product-size-options]');

    if (!data?.textContent) return [];

    try {
      const values = JSON.parse(data.textContent);

      if (!Array.isArray(values)) return [];

      return Array.from(new Set(values.map((value) => String(value).trim()).filter(Boolean)));
    } catch (error) {
      return [];
    }
  };

  const isSizeOrderedQuestion = (question = {}) => {
    const description = `${question.title || ''} ${question.question || ''}`.toLowerCase();

    return description.includes('size') && (description.includes('order') || description.includes('purchase'));
  };

  const getReviewQuestionOptions = (widget, question = {}) => {
    const configuredOptions = Array.isArray(question.options)
      ? question.options.map((option) => String(option).trim()).filter(Boolean)
      : [];

    if (!isSizeOrderedQuestion(question)) {
      return configuredOptions.map((option) => ({ label: option, value: option }));
    }

    const productSizeOptions = getProductSizeOptions(widget);

    if (!productSizeOptions.length) {
      return configuredOptions.map((option) => ({ label: option, value: option }));
    }

    const configuredByNormalizedValue = new Map(
      configuredOptions.map((option) => [option.toLowerCase(), option])
    );

    return productSizeOptions.map((size) => ({
      label: size,
      value: configuredByNormalizedValue.get(size.toLowerCase()) || size
    }));
  };

  const renderReviewQuestion = (question, widget) => {
    const questionId = getReviewQuestionId(question);
    const productId = getProductId(widget);
    const fieldType = String(question.fieldType ?? question.field_type ?? '').toLowerCase();
    const label = question.question || question.title || 'Review detail';
    const options = getReviewQuestionOptions(widget, question);
    const isRequired = Boolean(question.mandatory);
    const requiredAttribute = isRequired ? ' required' : '';
    const requiredLabel = isRequired ? '' : '<span>Optional</span>';
    const fieldName = `custom_field_${questionId}`;
    const fieldId = `YotpoReviewQuestion-${productId}-${questionId}`;

    if (!questionId) return '';

    if ((fieldType === 'size' || fieldType === 'rating') && options.length) {
      return `
        <fieldset class="yotpo-review-modal__question yotpo-review-modal__question--choices" data-yotpo-review-question="${escapeAttribute(questionId)}">
          <legend>${escapeHtml(label)} ${requiredLabel}</legend>
          <div class="yotpo-review-modal__choice-options">
            ${options.map((option, index) => `
              <label class="yotpo-review-modal__choice">
                <input type="radio" name="${escapeAttribute(fieldName)}" value="${index + 1}"${requiredAttribute}>
                <span>${escapeHtml(option.label)}</span>
              </label>
            `).join('')}
          </div>
        </fieldset>
      `;
    }

    if (fieldType === 'multiplechoice' && options.length) {
      return `
        <fieldset class="yotpo-review-modal__question yotpo-review-modal__question--choices" data-yotpo-review-question="${escapeAttribute(questionId)}">
          <legend>${escapeHtml(label)} ${requiredLabel}</legend>
          <div class="yotpo-review-modal__choice-options">
            ${options.map((option, index) => `
              <label class="yotpo-review-modal__choice">
                <input type="checkbox" name="${escapeAttribute(fieldName)}" value="${escapeAttribute(option.value)}"${isRequired && index === 0 ? ' required' : ''}>
                <span>${escapeHtml(option.label)}</span>
              </label>
            `).join('')}
          </div>
        </fieldset>
      `;
    }

    if (options.length) {
      return `
        <div class="yotpo-review-modal__field" data-yotpo-review-question="${escapeAttribute(questionId)}">
          <span class="yotpo-review-modal__field-label" id="${escapeAttribute(fieldId)}-label">${escapeHtml(label)} ${requiredLabel}</span>
          <input type="hidden" name="${escapeAttribute(fieldName)}" value="" data-yotpo-review-custom-select-input>
          <details class="yotpo-review-modal__custom-select" data-yotpo-review-custom-select>
            <summary aria-labelledby="${escapeAttribute(fieldId)}-label ${escapeAttribute(fieldId)}-value">
              <span id="${escapeAttribute(fieldId)}-value" data-yotpo-review-custom-select-value>Select an option</span>
              <svg class="icon" aria-hidden="true" focusable="false" viewBox="0 0 16 16">
                <path d="m4 6 4 4 4-4" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"></path>
              </svg>
            </summary>
            <div class="yotpo-review-modal__custom-select-panel" role="listbox" aria-labelledby="${escapeAttribute(fieldId)}-label">
              ${options.map((option) => `
                <button
                  class="yotpo-review-modal__custom-select-option"
                  type="button"
                  value="${escapeAttribute(option.value)}"
                  role="option"
                  aria-selected="false"
                  data-yotpo-review-custom-option
                >
                  ${escapeHtml(option.label)}
                </button>
              `).join('')}
            </div>
          </details>
        </div>
      `;
    }

    return `
      <div class="yotpo-review-modal__field" data-yotpo-review-question="${escapeAttribute(questionId)}">
        <label for="${escapeAttribute(fieldId)}">${escapeHtml(label)} ${requiredLabel}</label>
        <input id="${escapeAttribute(fieldId)}" type="text" name="${escapeAttribute(fieldName)}" maxlength="50"${requiredAttribute}>
      </div>
    `;
  };

  const setReviewQuestionsStatus = (widget, message = '') => {
    const status = widget.querySelector('[data-yotpo-review-questions-status]');

    if (!status) return;

    status.textContent = message;
    status.hidden = !message;
  };

  const renderReviewQuestions = (widget, questions = []) => {
    const section = widget.querySelector('[data-yotpo-review-questions]');
    const list = widget.querySelector('[data-yotpo-review-questions-list]');
    const renderableQuestions = questions.filter((question) => getReviewQuestionId(question));

    if (!section || !list) return;

    list.innerHTML = renderableQuestions.map((question) => renderReviewQuestion(question, widget)).join('');
    section.hidden = !renderableQuestions.length;
  };

  const loadReviewQuestions = async (widget) => {
    const state = widget.__yotpoReviewsState;

    if (!state || state.questionsLoaded || state.questionsLoading) return;

    state.questionsLoading = true;
    setReviewQuestionsStatus(widget, 'Loading fit details...');

    try {
      const response = await getProductFilters(state.appKey, state.productId);
      const questions = Array.isArray(response.customQuestions)
        ? response.customQuestions
        : Array.isArray(response.custom_questions)
          ? response.custom_questions
          : [];

      renderReviewQuestions(widget, questions);
      state.questionsLoaded = true;
      setReviewQuestionsStatus(widget, '');
    } catch (error) {
      setReviewQuestionsStatus(widget, 'Optional fit questions are temporarily unavailable. You can still submit your review.');
    } finally {
      state.questionsLoading = false;
    }
  };

  const getReviewPayload = (widget, form) => {
    const formData = new FormData(form);
    const payload = new URLSearchParams();

    payload.append('appkey', getAppKey(widget));
    payload.append('domain', widget.dataset.yotpoDomain || window.location.hostname);
    payload.append('sku', getProductId(widget));
    payload.append('product_title', widget.dataset.yotpoProductTitle || document.title);
    payload.append('product_description', widget.dataset.yotpoProductDescription || '');
    payload.append('product_url', widget.dataset.yotpoProductUrl || window.location.href);
    payload.append('product_image_url', widget.dataset.yotpoProductImage || '');
    payload.append('display_name', String(formData.get('display_name') || '').trim());
    payload.append('email', String(formData.get('email') || '').trim());
    payload.append('review_content', String(formData.get('review_content') || '').trim());
    payload.append('review_title', String(formData.get('review_title') || '').trim());
    payload.append('review_score', String(getNumber(formData.get('review_score'))));
    payload.append('review_source', 'widget_v3');
    payload.append('uploaded_images', '[]');

    form.querySelectorAll('[data-yotpo-review-question]').forEach((field) => {
      const questionId = String(field.dataset.yotpoReviewQuestion || '');
      const fieldName = `custom_field_${questionId}`;
      const answers = formData.getAll(fieldName)
        .map((answer) => String(answer).trim())
        .filter(Boolean);

      answers.forEach((answer) => {
        payload.append(`custom_fields[--${questionId}]${answers.length > 1 ? '[]' : ''}`, answer);
      });
    });

    return payload;
  };

  const submitReviewForm = async (widget, form) => {
    if (!form.reportValidity()) return;

    const success = widget.querySelector('[data-yotpo-review-success]');
    const successMessage = widget.querySelector('[data-yotpo-review-success-message]');
    const payload = getReviewPayload(widget, form);

    setReviewFeedback(form, '');
    setReviewFormLoading(form, true);

    try {
      await fetchYotpoJson('https://api.yotpo.com/v1/widget/reviews', {
        method: 'POST',
        body: payload
      });

      form.hidden = true;
      if (successMessage) {
        successMessage.textContent = 'Thanks for sharing your thoughts. Please check your email to verify the review with Yotpo.';
      }
      if (success) success.hidden = false;
    } catch (error) {
      setReviewFeedback(form, 'We could not submit your review right now. Please try again.');
    } finally {
      setReviewFormLoading(form, false);
    }
  };

  const getReviewModalFocusableElements = (modal) => {
    if (!modal) return [];

    return Array.from(
      modal.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => {
      return element instanceof HTMLElement && !element.hidden && element.offsetParent !== null;
    });
  };

  const isReviewModalOpen = (widget) => {
    const modal = widget.querySelector('[data-yotpo-review-modal]');

    return Boolean(modal && !modal.hidden);
  };

  const closeReviewModal = (widget, { restoreFocus = true } = {}) => {
    const modal = widget.querySelector('[data-yotpo-review-modal]');
    const state = widget.__yotpoReviewsState;

    if (!modal || modal.hidden) return;

    const existingTimer = reviewModalCloseTimers.get(modal);
    if (existingTimer) window.clearTimeout(existingTimer);

    modal.classList.remove('yotpo-review-modal--open');
    modal.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove(reviewModalOpenClass);
    document.body.classList.remove(reviewModalOpenClass);

    const closeTimer = window.setTimeout(() => {
      modal.hidden = true;
      reviewModalCloseTimers.delete(modal);
    }, 180);

    reviewModalCloseTimers.set(modal, closeTimer);

    if (restoreFocus && state?.reviewModalLastFocusedElement instanceof HTMLElement) {
      state.reviewModalLastFocusedElement.focus({ preventScroll: true });
    }
  };

  const openReviewModal = (widget) => {
    const state = widget.__yotpoReviewsState;
    const modal = widget.querySelector('[data-yotpo-review-modal]');

    if (!state || !modal) return;

    const existingTimer = reviewModalCloseTimers.get(modal);
    if (existingTimer) {
      window.clearTimeout(existingTimer);
      reviewModalCloseTimers.delete(modal);
    }

    state.reviewModalLastFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    resetReviewForm(widget);
    loadReviewQuestions(widget);
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add(reviewModalOpenClass);
    document.body.classList.add(reviewModalOpenClass);

    window.requestAnimationFrame(() => {
      modal.classList.add('yotpo-review-modal--open');
      const focusTarget = modal.querySelector('input[name="review_score"]') || modal.querySelector('[data-yotpo-review-close]') || modal;
      focusTarget.focus({ preventScroll: true });
    });
  };

  const trapReviewModalFocus = (event, widget) => {
    const modal = widget.querySelector('[data-yotpo-review-modal]');

    if (event.key !== 'Tab' || !modal || modal.hidden) return;

    const focusableElements = getReviewModalFocusableElements(modal);

    if (!focusableElements.length) {
      event.preventDefault();
      modal.focus({ preventScroll: true });
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
      return;
    }

    if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  };

  const getReviewUserName = (review = {}) => {
    return review.user?.displayName || review.user?.display_name || review.displayName || review.display_name || 'Anonymous';
  };

  const getReviewCreatedAt = (review = {}) => review.createdAt || review.created_at || '';

  const isVerifiedBuyer = (review = {}) => Boolean(review.verifiedBuyer ?? review.verified_buyer);

  const getReviewCustomFields = (review = {}) => review.customFields || review.custom_fields || {};

  const getReviewImages = (review = {}) => {
    if (Array.isArray(review.imagesData)) return review.imagesData;
    if (Array.isArray(review.images_data)) return review.images_data;

    return [];
  };

  const renderStarWidget = async (widget) => {
    const appKey = getAppKey(widget);
    const productId = getProductId(widget);
    const starRating = widget.querySelector('[data-yotpo-star-rating]');
    const stars = widget.querySelector('[data-yotpo-stars]');
    const text = widget.querySelector('[data-yotpo-star-text]');

    if (!appKey || !productId || !starRating || !text) return;

    try {
      const response = await getBottomline(appKey, productId);
      const bottomline = normalizeBottomline(response.bottomline);

      if (!bottomline.totalReviews) {
        starRating.hidden = true;
        updateProductReviewBadges(productId, bottomline);
        return;
      }

      setStars(stars, bottomline.averageScore);
      text.textContent = `${bottomline.averageScore.toFixed(1)} (${formatCount(bottomline.totalReviews)} ${pluralize(bottomline.totalReviews, 'review')})`;
      starRating.hidden = false;
      updateProductReviewBadges(productId, bottomline);
    } catch (error) {
      starRating.hidden = true;
      updateProductReviewBadges(productId);
    }
  };

  const renderDistribution = (widget, bottomline) => {
    const distributionRoot = widget.querySelector('[data-yotpo-distribution]');

    if (!distributionRoot) return;

    const total = Math.max(bottomline.totalReviews, 1);
    const activeStar = String(widget.__yotpoReviewsState?.star || '');

    distributionRoot.innerHTML = [5, 4, 3, 2, 1].map((star) => {
      const count = getNumber(bottomline.starDistribution[String(star)]);
      const percent = Math.min(Math.max((count / total) * 100, 0), 100);
      const isActive = activeStar === String(star);

      return `
        <button class="yotpo-api-distribution__row${isActive ? ' is-active' : ''}" type="button" data-yotpo-filter-star="${star}" aria-pressed="${isActive}">
          <span class="yotpo-api-distribution__label">${star} ${pluralize(star, 'star')}</span>
          <span class="yotpo-api-distribution__bar" aria-hidden="true">
            <span style="width: ${percent}%"></span>
          </span>
          <span class="yotpo-api-distribution__count">${formatCount(count)}</span>
        </button>
      `;
    }).join('');
  };

  const renderSummary = (widget, bottomline) => {
    const summary = widget.querySelector('[data-yotpo-summary]');
    const average = widget.querySelector('[data-yotpo-average]');
    const stars = widget.querySelector('[data-yotpo-summary-stars]');
    const count = widget.querySelector('[data-yotpo-summary-count]');

    if (!summary || !average || !count) return;

    if (!bottomline.totalReviews) {
      summary.hidden = true;
      return;
    }

    average.textContent = bottomline.averageScore.toFixed(1);
    count.textContent = `${formatCount(bottomline.totalReviews)} ${pluralize(bottomline.totalReviews, 'review')}`;
    setStars(stars, bottomline.averageScore);
    renderDistribution(widget, bottomline);
    summary.hidden = false;
  };

  const renderCustomFields = (fields) => {
    const fitLabels = {
      1: 'Runs small',
      2: 'True to size',
      3: 'Runs large'
    };
    const entries = Object.entries(fields || {})
      .map(([key, field]) => {
        if (!field) return null;

        if (typeof field === 'object') {
          return {
            title: field.title || key,
            value: field.value
          };
        }

        return {
          title: key,
          value: field
        };
      })
      .filter((field) => {
        return field && field.title && field.value !== undefined && field.value !== null && field.value !== '';
      });

    if (!entries.length) return '';

    return `
      <dl class="yotpo-api-review__fields">
        ${entries.slice(0, 6).map((field) => {
          const value = typeof field.value === 'number' && String(field.title).toLowerCase() === 'fit'
            ? fitLabels[field.value] || field.value
            : field.value;

          return `
            <div>
              <dt>${escapeHtml(field.title)}</dt>
              <dd>${escapeHtml(value)}</dd>
            </div>
          `;
        }).join('')}
      </dl>
    `;
  };

  const renderReviewImages = (review) => {
    const images = getReviewImages(review);

    if (!images.length) return '';

    const title = review.title || 'customer review';

    return `
      <div class="yotpo-api-review__images">
        ${images.slice(0, 4).map((image) => {
          const originalUrl = image.originalUrl || image.original_url || image.thumbUrl || image.thumb_url || '';
          const thumbUrl = image.thumbUrl || image.thumb_url || image.originalUrl || image.original_url || '';

          if (!thumbUrl) return '';

          return `
            <a href="${escapeAttribute(originalUrl || thumbUrl)}" target="_blank" rel="noopener">
              <img src="${escapeAttribute(thumbUrl)}" alt="Customer photo from ${escapeAttribute(title)}" loading="lazy">
            </a>
          `;
        }).join('')}
      </div>
    `;
  };

  const renderReviewComment = (comment) => {
    if (!comment?.content) return '';

    return `
      <aside class="yotpo-api-review__comment">
        <span class="yotpo-api-review__comment-avatar" aria-hidden="true">WO</span>
        <div>
          <strong>Wild Oak Boutique</strong>
          <p>${escapeHtml(comment.content).replace(/\n/g, '<br>')}</p>
        </div>
      </aside>
    `;
  };

  const getInitials = (name) => {
    const words = String(name || '')
      .replace(/[^a-zA-Z0-9\s.]/g, ' ')
      .split(/\s+/)
      .filter(Boolean);

    if (!words.length) return 'A';

    const first = words[0]?.[0] || '';
    const second = words.length > 1 ? words[words.length - 1]?.[0] : words[0]?.[1] || '';

    return `${first}${second}`.toUpperCase();
  };

  const renderReviewActions = (review) => {
    const votesUp = getNumber(review.votesUp ?? review.votes_up);
    const votesDown = getNumber(review.votesDown ?? review.votes_down);
    const actions = [];

    if (votesUp > 0) actions.push(`${formatCount(votesUp)} found helpful`);
    if (votesDown > 0) actions.push(`${formatCount(votesDown)} disagreed`);

    actions.push(`Review #${review.id}`);

    return `
      <div class="yotpo-api-review__actions" aria-label="Review metadata">
        ${actions.map((action) => `<span>${escapeHtml(action)}</span>`).join('')}
      </div>
    `;
  };

  const renderVerifiedBadge = () => {
    return `
      <span class="yotpo-api-review__badge yotpo-api-review__badge--verified">
        <svg class="yotpo-api-review__badge-icon" aria-hidden="true" focusable="false" viewBox="0 0 16 16">
          <path fill="#1d9bf0" d="M8 1.15 9.55 2.1l1.8-.15.78 1.64 1.64.78-.15 1.8.95 1.55-.95 1.55.15 1.8-1.64.78-.78 1.64-1.8-.15L8 14.85l-1.55-.95-1.8.15-.78-1.64-1.64-.78.15-1.8-.95-1.55.95-1.55-.15-1.8 1.64-.78.78-1.64 1.8.15L8 1.15Z"></path>
          <path d="M5.15 8.15 6.9 9.9l3.95-4" fill="none" stroke="#fff" stroke-width="1.65" stroke-linecap="round" stroke-linejoin="round"></path>
        </svg>
        Verified buyer
      </span>
    `;
  };

  const renderReview = (review) => {
    const reviewerName = getReviewUserName(review);
    const createdAt = formatDate(getReviewCreatedAt(review));
    const title = review.title || 'Review';
    const score = getNumber(review.score);
    const initials = getInitials(reviewerName);
    const badges = [
      isVerifiedBuyer(review) ? renderVerifiedBadge() : ''
    ].filter(Boolean);

    return `
      <article class="yotpo-api-review">
        <div class="yotpo-api-review__rail" aria-hidden="true">
          <span class="yotpo-api-review__avatar">${escapeHtml(initials)}</span>
          <span class="yotpo-api-review__threadline"></span>
        </div>
        <div class="yotpo-api-review__body">
          <header class="yotpo-api-review__header">
            <div class="yotpo-api-review__identity">
              <strong>${escapeHtml(reviewerName)}</strong>
              ${createdAt ? `<span>${escapeHtml(createdAt)}</span>` : ''}
            </div>
            <span class="yotpo-api-stars" style="--rating-percent: ${(score / 5) * 100}%;" aria-label="${score.toFixed(1)} out of 5 stars"></span>
          </header>
          ${badges.length ? `<div class="yotpo-api-review__badges">${badges.join('')}</div>` : ''}
          <h3>${escapeHtml(title)}</h3>
          ${review.content ? `<p class="yotpo-api-review__content">${escapeHtml(review.content).replace(/\n/g, '<br>')}</p>` : ''}
          ${renderCustomFields(getReviewCustomFields(review))}
          ${renderReviewImages(review)}
          ${renderReviewComment(review.comment)}
          ${renderReviewActions(review)}
        </div>
      </article>
    `;
  };

  const setActiveFilter = (widget) => {
    const activeStar = String(widget.__yotpoReviewsState?.star || '');

    widget.querySelectorAll('[data-yotpo-filter-star]').forEach((button) => {
      const isActive = String(button.dataset.yotpoFilterStar || '') === activeStar;

      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', String(isActive));
    });
  };

  const updateReviewActions = (widget) => {
    const toolbar = widget.querySelector('[data-yotpo-toolbar]');
    const writeReview = widget.querySelector('[data-yotpo-write-review]');

    if (toolbar) toolbar.hidden = false;

    if (writeReview) {
      writeReview.hidden = false;
    }
  };

  const injectStructuredData = (widget, bottomline, reviews) => {
    const productId = getProductId(widget);

    if (!productId || !bottomline.totalReviews) return;

    const scriptId = `YotpoApiReviewsJsonLd-${productId}`;
    const existingScript = document.getElementById(scriptId);
    const productData = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: widget.dataset.yotpoProductTitle || '',
      url: widget.dataset.yotpoProductUrl || window.location.href,
      image: widget.dataset.yotpoProductImage || undefined,
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: Number(bottomline.averageScore.toFixed(2)),
        reviewCount: bottomline.totalReviews
      },
      review: reviews.slice(0, 5).map((review) => ({
        '@type': 'Review',
        author: {
          '@type': 'Person',
          name: getReviewUserName(review)
        },
        datePublished: getReviewCreatedAt(review),
        name: review.title || undefined,
        reviewBody: review.content || undefined,
        reviewRating: {
          '@type': 'Rating',
          ratingValue: getNumber(review.score),
          bestRating: 5,
          worstRating: 1
        }
      }))
    };

    const script = existingScript || document.createElement('script');

    script.id = scriptId;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(productData);

    if (!existingScript) document.head.appendChild(script);
  };

  const renderReviewsState = (widget, response, { append = false } = {}) => {
    const state = widget.__yotpoReviewsState;
    const reviewList = widget.querySelector('[data-yotpo-review-list]');
    const footer = widget.querySelector('[data-yotpo-footer]');
    const loadMore = widget.querySelector('[data-yotpo-load-more]');
    const reviews = Array.isArray(response.reviews) ? response.reviews : [];
    const pagination = response.pagination || {};
    const responseBottomline = normalizeBottomline(response.bottomline);

    if (!reviewList || !state) return;

    if (!append) {
      state.reviews = [];
      reviewList.innerHTML = '';
    }

    state.page = getNumber(pagination.page, state.page);
    state.total = getNumber(pagination.total, reviews.length);
    state.reviews = [...state.reviews, ...reviews];

    if (!state.baseBottomline && responseBottomline.totalReviews) {
      state.baseBottomline = responseBottomline;
    }

    renderSummary(widget, state.baseBottomline || responseBottomline);
    updateProductReviewBadges(state.productId, state.baseBottomline || responseBottomline);
    updateReviewActions(widget, response);
    setActiveFilter(widget);

    if (!state.total) {
      setStatus(widget, state.star ? `No ${state.star}-star reviews yet.` : 'No reviews yet.');
      if (footer) footer.hidden = true;
      if (loadMore) {
        loadMore.disabled = false;
        loadMore.textContent = 'Load more reviews';
      }
      return;
    }

    setStatus(widget, '');
    reviewList.insertAdjacentHTML('beforeend', reviews.map(renderReview).join(''));
    injectStructuredData(widget, state.baseBottomline || responseBottomline, state.reviews);

    const hasMore = state.reviews.length < state.total;

    if (footer) footer.hidden = !hasMore;
    if (loadMore) {
      loadMore.disabled = false;
      loadMore.textContent = hasMore ? 'Load more reviews' : 'All reviews loaded';
    }
  };

  const loadReviews = async (widget, { reset = false } = {}) => {
    const state = widget.__yotpoReviewsState;

    if (!state || state.loading) return;

    state.loading = true;

    const loadMore = widget.querySelector('[data-yotpo-load-more]');
    if (loadMore) {
      loadMore.disabled = true;
      loadMore.textContent = 'Loading...';
    }

    if (reset) {
      state.page = 1;
      state.total = 0;
      state.reviews = [];
      setStatus(widget, 'Loading reviews...');
      widget.querySelector('[data-yotpo-review-list]').innerHTML = '';
    }

    try {
      const response = await getReviewsPage({
        appKey: state.appKey,
        productId: state.productId,
        page: state.page,
        perPage: state.perPage,
        star: state.star
      });

      renderReviewsState(widget, response, { append: !reset });
      state.page += 1;
    } catch (error) {
      setStatus(widget, 'Reviews are temporarily unavailable.');
      if (loadMore) {
        loadMore.disabled = false;
        loadMore.textContent = 'Load more reviews';
      }
    } finally {
      state.loading = false;
    }
  };

  const initReviewsWidget = (widget) => {
    const appKey = getAppKey(widget);
    const productId = getProductId(widget);

    if (!appKey || !productId) return;

    widget.__yotpoReviewsState = {
      appKey,
      productId,
      perPage: Math.min(Math.max(getNumber(widget.dataset.yotpoPerPage, 5), 1), 20),
      page: 1,
      total: 0,
      star: '',
      reviews: [],
      baseBottomline: null,
      reviewModalLastFocusedElement: null,
      questionsLoaded: false,
      questionsLoading: false,
      loading: false
    };

    widget.addEventListener('click', (event) => {
      const filterButton = event.target.closest('[data-yotpo-filter-star]');
      const loadMoreButton = event.target.closest('[data-yotpo-load-more]');
      const writeReviewButton = event.target.closest('[data-yotpo-write-review]');
      const reviewModalClose = event.target.closest('[data-yotpo-review-close]');
      const reviewCustomOption = event.target.closest('[data-yotpo-review-custom-option]');

      if (reviewCustomOption && widget.contains(reviewCustomOption)) {
        event.preventDefault();

        const customSelect = reviewCustomOption.closest('[data-yotpo-review-custom-select]');
        const field = customSelect?.closest('[data-yotpo-review-question]');
        const input = field?.querySelector('[data-yotpo-review-custom-select-input]');
        const valueLabel = customSelect?.querySelector('[data-yotpo-review-custom-select-value]');

        if (input instanceof HTMLInputElement) {
          input.value = reviewCustomOption.value;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }

        if (valueLabel) valueLabel.textContent = reviewCustomOption.textContent.trim();

        customSelect?.querySelectorAll('[data-yotpo-review-custom-option]').forEach((option) => {
          option.setAttribute('aria-selected', String(option === reviewCustomOption));
        });

        if (customSelect instanceof HTMLDetailsElement) {
          customSelect.open = false;
          customSelect.querySelector('summary')?.focus({ preventScroll: true });
        }

        return;
      }

      if (filterButton && widget.contains(filterButton)) {
        event.preventDefault();
        widget.__yotpoReviewsState.star = filterButton.dataset.yotpoFilterStar || '';
        setActiveFilter(widget);
        renderDistribution(widget, widget.__yotpoReviewsState.baseBottomline || normalizeBottomline());
        loadReviews(widget, { reset: true });
        return;
      }

      if (writeReviewButton && widget.contains(writeReviewButton)) {
        event.preventDefault();
        openReviewModal(widget);
        return;
      }

      if (reviewModalClose && widget.contains(reviewModalClose)) {
        event.preventDefault();
        closeReviewModal(widget);
        return;
      }

      if (loadMoreButton && widget.contains(loadMoreButton)) {
        event.preventDefault();
        loadReviews(widget);
      }
    });

    widget.addEventListener('change', (event) => {
      const ratingInput = event.target.closest('input[name="review_score"]');

      if (ratingInput && widget.contains(ratingInput)) {
        updateReviewRatingState(ratingInput.form);
      }
    });

    widget.addEventListener('submit', (event) => {
      const form = event.target.closest('[data-yotpo-review-form]');

      if (form && widget.contains(form)) {
        event.preventDefault();
        submitReviewForm(widget, form);
      }
    });

    document.addEventListener('keydown', (event) => {
      if (!isReviewModalOpen(widget)) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        closeReviewModal(widget);
        return;
      }

      trapReviewModalFocus(event, widget);
    });

    loadReviews(widget, { reset: true });
    loadReviewQuestions(widget);
  };

  widgets.forEach((widget) => {
    const widgetType = widget.dataset.yotpoWidgetType;

    if (widgetType === 'star_rating' || widgetType === 'star-rating') {
      renderStarWidget(widget);
      return;
    }

    initReviewsWidget(widget);
  });
})();
